// src/pages/sessions/components/LiveSession/managers/RoomConnectionManager.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { ConnectionState } from 'livekit-client';
import { toast } from 'sonner';
import sessionService from '@/services/sessionService';

// CleanupTracker class - should be moved to a shared utilities file
class CleanupTracker {
  constructor() {
    this.timeouts = new Set();
    this.intervals = new Set();
    this.listeners = new Map();
    this.subscriptions = new Set();
    this.animationFrames = new Set();
    this.disposed = false;
  }

  setTimeout(fn, delay) {
    if (this.disposed) return null;
    const id = setTimeout(() => {
      this.timeouts.delete(id);
      if (!this.disposed) fn();
    }, delay);
    this.timeouts.add(id);
    return id;
  }

  addSubscription(cleanup) {
    if (!this.disposed) this.subscriptions.add(cleanup);
  }

  cleanup() {
    this.disposed = true;
    this.timeouts.forEach(id => clearTimeout(id));
    this.timeouts.clear();
    this.intervals.forEach(id => clearInterval(id));
    this.intervals.clear();
    this.animationFrames.forEach(id => cancelAnimationFrame(id));
    this.animationFrames.clear();
    this.listeners.forEach(cleanup => {
      try {
        if (typeof cleanup === 'function') cleanup();
      } catch (e) {
        console.debug('Listener cleanup error:', e);
      }
    });
    this.listeners.clear();
    this.subscriptions.forEach(cleanup => {
      try {
        cleanup();
      } catch (e) {
        console.debug('Subscription cleanup error:', e);
      }
    });
    this.subscriptions.clear();
  }
}

// Safe metadata update logic
const useSafeMetadataUpdate = () => {
  const pendingUpdatesRef = useRef([]);
  const updateTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastMetadataRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const sidCheckCountRef = useRef(0);
  
  const updateMetadata = useCallback(async (room, metadata) => {
    // Multiple safety checks
    if (!room?.localParticipant) return false;
    
    // Check if we're already updating
    if (isUpdatingRef.current) {
      pendingUpdatesRef.current = [metadata];
      return false;
    }
    
    // Check room connection state
    if (room.state !== ConnectionState.Connected) {
      pendingUpdatesRef.current = [metadata];
      return false;
    }
    
    // CRITICAL: Wait for SID with retry logic
    if (!room.localParticipant.sid) {
      pendingUpdatesRef.current = [metadata];
      
      // Retry with exponential backoff if no SID yet
      if (sidCheckCountRef.current < 10) {
        sidCheckCountRef.current++;
        const delay = Math.min(1000 * Math.pow(1.5, sidCheckCountRef.current), 10000);
        
        console.log(`No SID yet, retrying in ${delay}ms (attempt ${sidCheckCountRef.current})`);
        
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }
        
        updateTimeoutRef.current = setTimeout(() => {
          if (room.state === ConnectionState.Connected) {
            updateMetadata(room, metadata);
          }
        }, delay);
      }
      
      return false;
    }
    
    // Reset SID check counter once we have a SID
    sidCheckCountRef.current = 0;
    
    // Check if metadata has actually changed
    const metadataStr = JSON.stringify(metadata);
    if (metadataStr === lastMetadataRef.current) {
      return true; // No change needed
    }
    
    // Prevent concurrent updates
    isUpdatingRef.current = true;
    
    try {
      // Add a timeout wrapper around the metadata update
      const updatePromise = room.localParticipant.setMetadata(metadataStr);
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Metadata update timeout')), 10000);
      });
      
      await Promise.race([updatePromise, timeoutPromise]);
      
      lastMetadataRef.current = metadataStr;
      retryCountRef.current = 0;
      isUpdatingRef.current = false;
      return true;
    } catch (error) {
      isUpdatingRef.current = false;
      
      if (error.message?.includes('timeout') || error.message?.includes('before connected')) {
        // Connection issue - queue for retry
        pendingUpdatesRef.current = [metadata];
        
        // Retry with exponential backoff
        if (retryCountRef.current < 5) {
          retryCountRef.current++;
          const delay = Math.min(Math.pow(2, retryCountRef.current) * 1000, 16000);
          
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }
          
          updateTimeoutRef.current = setTimeout(() => {
            if (room.state === ConnectionState.Connected && room.localParticipant.sid) {
              updateMetadata(room, metadata);
            }
          }, delay);
        } else {
          console.warn('Max retries reached for metadata update');
          retryCountRef.current = 0;
        }
        
        return false;
      }
      
      console.error('Metadata update failed:', error);
      return false;
    }
  }, []);
  
  const processPendingUpdates = useCallback((room) => {
    if (pendingUpdatesRef.current.length > 0 && 
        room?.state === ConnectionState.Connected && 
        !isUpdatingRef.current) {
      const metadata = pendingUpdatesRef.current.shift();
      if (metadata) {
        updateMetadata(room, metadata);
      }
    }
  }, [updateMetadata]);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      isUpdatingRef.current = false;
    };
  }, []);
  
  return { updateMetadata, processPendingUpdates };
};

export function useRoomConnectionManager({
  sessionId,
  user,
  isTeacherUser,
  savedVirtualRoomIdRef,
  preloadedRoomAssignment,
  setTeacherBroadcastRooms,
  setTeacherMonitorRooms,
  teacherBroadcastRooms,
  teacherMonitorRooms,
  setVideoEnabled,
  setAudioEnabled,
  setIsConnected
}) {
  const [roomConnected, setRoomConnected] = useState(false);
  const [connectionState, setConnectionState] = useState(ConnectionState.Disconnected);
  const [localParticipantIdentity, setLocalParticipantIdentity] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const roomRef = useRef(null);
  const cleanupTrackerRef = useRef(new CleanupTracker());
  const mountedRef = useRef(true);
  const roomInitializedRef = useRef(false);
  const broadcastStateLoadedRef = useRef(false);
  const screenSharerNamesRef = useRef({});
  const lastBroadcastStateRef = useRef({ broadcastingTo: [], monitoringRooms: [] });
  
  const cleanupTracker = cleanupTrackerRef.current;
  const { updateMetadata, processPendingUpdates } = useSafeMetadataUpdate();

  // Initialize room when connected
  const initializeRoom = useCallback(async (room) => {
    if (!room || roomInitializedRef.current || !mountedRef.current) return;
    
    roomInitializedRef.current = true;
    roomRef.current = room;
    
    if (room.localParticipant?.identity) {
      setLocalParticipantIdentity(room.localParticipant.identity);
      
      const ourName = user?.displayName || user?.email || 'You';
      screenSharerNamesRef.current[room.localParticipant.identity] = ourName;
      
      // Load teacher broadcast state if teacher
      let broadcastingTo = [];
      let monitoringRooms = [];
      
      if (isTeacherUser && !broadcastStateLoadedRef.current) {
        try {
          const savedState = await sessionService.getTeacherBroadcastState(sessionId);
          if (mountedRef.current && savedState?.success && savedState?.data) {
            broadcastingTo = savedState.data.broadcastingTo || [];
            monitoringRooms = savedState.data.monitoringRooms || [];
            
            setTeacherBroadcastRooms(new Set(broadcastingTo));
            setTeacherMonitorRooms(new Set(monitoringRooms));
            broadcastStateLoadedRef.current = true;
            
            // Store the loaded state
            lastBroadcastStateRef.current = { broadcastingTo, monitoringRooms };
          }
        } catch (error) {
          console.error('Failed to load broadcast state on connect:', error);
        }
      }
      
      // Use the correct virtual room ID
      let virtualRoomId = null;
      
      if (!isTeacherUser) {
        virtualRoomId = preloadedRoomAssignment !== undefined && preloadedRoomAssignment !== null 
          ? preloadedRoomAssignment 
          : (savedVirtualRoomIdRef?.current !== undefined ? savedVirtualRoomIdRef.current : null);
        
        if (preloadedRoomAssignment !== undefined && preloadedRoomAssignment !== null) {
          savedVirtualRoomIdRef.current = preloadedRoomAssignment;
        }
      } else {
        virtualRoomId = null;
      }
      
      console.log('Setting initial metadata with virtualRoomId:', virtualRoomId, 
                  'preloaded:', preloadedRoomAssignment, 
                  'savedRef:', savedVirtualRoomIdRef?.current);
      
      const metadata = {
        role: isTeacherUser ? 'teacher' : 'student',
        userId: user?.id,
        displayName: ourName,
        institutionMemberId: user?.institutionMemberId,
        virtualRoomId: virtualRoomId,
        ...(isTeacherUser && {
          broadcastingTo: Array.from(broadcastingTo),
          monitoringRooms: Array.from(monitoringRooms)
        })
      };
      
      // Use safe metadata update with shorter delay for initial metadata
      cleanupTracker.setTimeout(() => {
        if (mountedRef.current && room.state === ConnectionState.Connected) {
          updateMetadata(room, metadata);
        }
      }, 100);
      
      // Set initial media states
      const videoTrack = room.localParticipant.getTrackPublication('camera');
      const audioTrack = room.localParticipant.getTrackPublication('microphone');
      
      if (videoTrack) setVideoEnabled(!videoTrack.isMuted);
      if (audioTrack) setAudioEnabled(!audioTrack.isMuted);
    }
    
    console.log('Room connection initialized successfully');
    toast.success('Connected to session');
  }, [sessionId, user, isTeacherUser, savedVirtualRoomIdRef, preloadedRoomAssignment,
      setTeacherBroadcastRooms, setTeacherMonitorRooms, setVideoEnabled, 
      setAudioEnabled, updateMetadata, cleanupTracker]);

  // ADDED: Force disconnect existing connection to prevent duplicate identity errors
  const forceDisconnectExisting = useCallback(async () => {
    if (!roomRef.current) return;
    
    const room = roomRef.current;
    
    // Don't disconnect if already disconnected
    if (room.state === ConnectionState.Disconnected) {
      return;
    }
    
    console.log('🔌 Force disconnecting existing room connection...');
    
    try {
      // Remove all listeners first to prevent memory leaks
      room.removeAllListeners();
      
      // Disconnect from the room
      await room.disconnect();
      
      // Clear refs
      roomRef.current = null;
      roomInitializedRef.current = false;
      broadcastStateLoadedRef.current = false;
      
      console.log('✅ Existing connection disconnected successfully');
      
      // Safari needs extra time to properly close WebRTC connections
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const delay = isSafari ? 2000 : 500;
      
      console.log(`⏳ Waiting ${delay}ms for connection cleanup (Safari: ${isSafari})...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      
    } catch (error) {
      console.error('❌ Error during force disconnect:', error);
      // Still clear the ref even if disconnect fails
      roomRef.current = null;
      roomInitializedRef.current = false;
    }
  }, []);

  // Update metadata when needed - CRITICAL FIX to always preserve broadcast state
  const updateParticipantMetadata = useCallback(async (updates) => {
    if (!roomRef.current?.localParticipant || !mountedRef.current) return;
    
    // Always preserve the virtual room ID unless explicitly updating it
    let virtualRoomId;
    
    if (updates.hasOwnProperty('virtualRoomId')) {
      virtualRoomId = updates.virtualRoomId;
    } else {
      virtualRoomId = savedVirtualRoomIdRef?.current !== undefined 
        ? savedVirtualRoomIdRef.current 
        : null;
    }
    
    // Build the complete metadata object
    const currentMetadata = {
      role: isTeacherUser ? 'teacher' : 'student',
      userId: user?.id,
      displayName: user?.displayName || user?.email,
      institutionMemberId: user?.institutionMemberId,
      virtualRoomId: virtualRoomId,
    };
    
    // CRITICAL FIX: For teachers, ALWAYS include broadcast state
    if (isTeacherUser) {
      // If updates include broadcast state, use it and store it
      if (updates.hasOwnProperty('broadcastingTo')) {
        currentMetadata.broadcastingTo = updates.broadcastingTo;
        lastBroadcastStateRef.current.broadcastingTo = updates.broadcastingTo;
      } else {
        // Use current state or last known state
        currentMetadata.broadcastingTo = teacherBroadcastRooms 
          ? Array.from(teacherBroadcastRooms) 
          : lastBroadcastStateRef.current.broadcastingTo;
      }
      
      if (updates.hasOwnProperty('monitoringRooms')) {
        currentMetadata.monitoringRooms = updates.monitoringRooms;
        lastBroadcastStateRef.current.monitoringRooms = updates.monitoringRooms;
      } else {
        // Use current state or last known state
        currentMetadata.monitoringRooms = teacherMonitorRooms 
          ? Array.from(teacherMonitorRooms) 
          : lastBroadcastStateRef.current.monitoringRooms;
      }
    }
    
    // Merge any other updates
    Object.keys(updates).forEach(key => {
      if (!['virtualRoomId', 'broadcastingTo', 'monitoringRooms'].includes(key)) {
        currentMetadata[key] = updates[key];
      }
    });
    
    console.log('Updating participant metadata:', currentMetadata);
    
    await updateMetadata(roomRef.current, currentMetadata);
  }, [user, isTeacherUser, savedVirtualRoomIdRef, teacherBroadcastRooms, 
      teacherMonitorRooms, updateMetadata]);

  // Track connection state changes and update global store
  const handleConnectionStateChange = useCallback((state) => {
    console.log('Connection state changing to:', state);
    setConnectionState(state);
    
    if (state === ConnectionState.Connected) {
      console.log('Room connected, updating store');
      setRoomConnected(true);
      if (setIsConnected) {
        setIsConnected(true);
      }
      if (roomRef.current) {
        processPendingUpdates(roomRef.current);
      }
    } else if (state === ConnectionState.Disconnected) {
      console.log('Room disconnected, updating store');
      setRoomConnected(false);
      if (setIsConnected) {
        setIsConnected(false);
      }
      roomInitializedRef.current = false;
      broadcastStateLoadedRef.current = false;
    } else if (state === ConnectionState.Reconnecting) {
      console.log('Room reconnecting');
      setRoomConnected(false);
      if (setIsConnected) {
        setIsConnected(false);
      }
    }
  }, [processPendingUpdates, setIsConnected]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    
    return () => {
      mountedRef.current = false;
      cleanupTracker.cleanup();
      roomInitializedRef.current = false;
      broadcastStateLoadedRef.current = false;
    };
  }, [cleanupTracker]);

  return {
    roomRef,
    roomConnected,
    connectionState,
    localParticipantIdentity,
    isInitializing,
    screenSharerNamesRef,
    initializeRoom,
    updateMetadata,
    processPendingUpdates,
    updateParticipantMetadata,
    handleConnectionStateChange,
    forceDisconnectExisting, // ADDED: Export the new force disconnect method
  };
}